// client/src/pages/secure/SystemStatusPage.js — Administration → System status.
//
// Is the office search computer checking in, how did its recent runs go, where does each
// client site search stand (with a Retry button), and what the other background jobs last
// did. Everything here is read from the server (/api/ops-status); the only action is Retry,
// which is the same "Run search now" request the Site search panel makes.

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import DataList from '../../components/shared/DataList';

const TONE = {
    green: { dot: 'bg-green-500', box: 'border-green-200 bg-green-50', text: 'text-green-900', label: 'OK' },
    amber: { dot: 'bg-amber-500', box: 'border-amber-200 bg-amber-50', text: 'text-amber-900', label: 'Needs a look' },
    red: { dot: 'bg-red-500', box: 'border-red-200 bg-red-50', text: 'text-red-900', label: 'Problem' },
    grey: { dot: 'bg-gray-400', box: 'border-gray-200 bg-gray-50', text: 'text-gray-700', label: 'Not set up' },
};
const Dot = ({ tone }) => <span className={`inline-block h-3 w-3 shrink-0 rounded-full ${(TONE[tone] || TONE.grey).dot}`} aria-label={(TONE[tone] || TONE.grey).label} />;
const fmt = (d) => (d ? new Date(d).toLocaleString() : '—');
const FREQ = { daily: 'Daily', weekly: 'Weekly', biweekly: 'Every other week', monthly: 'Monthly', manual: 'Only when asked' };

const SystemStatusPage = () => {
    const api = useApiClient();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [showIdle, setShowIdle] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [notice, setNotice] = useState('');

    const load = useCallback(async () => {
        try { setData(await api.get(`/api/ops-status${showIdle ? '?include_idle=1' : ''}`)); setError(''); }
        catch (e) { setError(e.message); }
    }, [api, showIdle]);
    useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

    const retry = async (s) => {
        setBusyId(s.search_id);
        try { const r = await api.post(`/api/site-searches/${s.search_id}/run-now`, {}); setNotice(`${s.title}: ${r.message}`); load(); }
        catch (e) { setNotice(`${s.title}: ${e.message}`); }
        finally { setBusyId(null); }
    };

    if (!data) return <div className="p-8">{error ? <span className="text-red-600">{error}</span> : 'Loading system status…'}</div>;
    const { health, runs, searches } = data.site_search;
    const t = TONE[health.tone] || TONE.grey;

    return (
        <div className="space-y-6 p-4 md:p-8">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800 md:text-3xl">System status</h1>
                    <p className="text-sm text-slate-600">What runs in the background, and whether it is working. Refreshes every minute.</p>
                </div>
                <button onClick={load} className="rounded border border-slate-300 px-3 py-1 text-sm">Refresh</button>
            </div>
            {error && <p className="rounded bg-red-50 p-3 text-sm text-red-700">{error}</p>}
            {notice && <p className="rounded bg-indigo-50 p-3 text-sm text-indigo-800">{notice}</p>}

            {/* The office search computer */}
            <section className={`rounded-lg border p-4 md:p-6 ${t.box}`}>
                <div className="flex items-start gap-3">
                    <span className="mt-1.5"><Dot tone={health.tone} /></span>
                    <div>
                        <h2 className={`text-lg font-semibold ${t.text}`}>{health.headline}</h2>
                        <p className={`text-sm ${t.text}`}>{health.advice}</p>
                        <p className="mt-1 text-xs text-slate-500">
                            The office computer runs the client site searches. Green: checked in within 45 minutes. Amber: missed a check, or its last run did not finish. Red: not seen for over 75 minutes.
                        </p>
                    </div>
                </div>
            </section>

            {/* Recent runs */}
            <section className="rounded-lg bg-white p-4 shadow md:p-6">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <h2 className="text-lg font-semibold text-slate-800">Recent search runs</h2>
                    <label className="flex items-center gap-2 text-sm text-slate-600">
                        <input type="checkbox" checked={showIdle} onChange={(e) => setShowIdle(e.target.checked)} />
                        Also show check-ins where nothing was due
                    </label>
                </div>
                {!runs.length ? <p className="text-sm text-slate-500">No runs reported yet.</p> : (
                    <ul className="divide-y divide-slate-100">
                        {runs.map((r) => (
                            <li key={r.heartbeat_id} className="flex items-start gap-3 py-2 text-sm">
                                <span className="mt-1"><Dot tone={r.tone} /></span>
                                <span className="w-36 shrink-0 text-slate-500">{r.when}</span>
                                <span className="text-slate-800">{r.text}{r.due_ids ? <span className="text-slate-400"> · search #{r.due_ids.split(' ').join(', #')}</span> : null}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {/* Site searches */}
            <section className="rounded-lg bg-white p-4 shadow md:p-6">
                <h2 className="mb-1 text-lg font-semibold text-slate-800">Client site searches</h2>
                <p className="mb-3 text-sm text-slate-600">
                    <b>Retry</b> queues the search for the office computer's next check (the same as <i>Run search now</i> on the client's page). A search that did not finish stays queued and is retried by itself.
                </p>
                {!searches.length ? <p className="text-sm text-slate-500">No open site searches.</p> : (
                    <div className="overflow-x-auto">
                        <DataList rows={searches} rowKey="search_id" csvName="site-search-status" defaultSort={{ key: 'last_swept_at', dir: 'desc' }}
                            filters={[{ key: 'freq', label: 'Runs' }, { key: 'state', label: 'Now' }]}
                            columns={[
                                { key: 'client_name', label: 'Client / search', value: (s) => `${s.client_name || ''} — ${s.title || ''}`, sortValue: (s) => s.client_name || '',
                                    render: (s) => <><Link to={`/dashboard/clients/${s.client_id}`} className="font-medium text-indigo-700 hover:underline">{s.client_name}</Link><div className="text-slate-600">{s.title}</div></> },
                                { key: 'freq', label: 'Runs', value: (s) => FREQ[s.sweep_frequency] || s.sweep_frequency },
                                { key: 'last_swept_at', label: 'Last result', type: 'date', value: (s) => `${s.last_swept_at ? fmt(s.last_swept_at) : 'Never run'}${s.last_sweep_summary ? ` — ${s.last_sweep_summary}` : ''}`,
                                    sortValue: (s) => s.last_swept_at || null,
                                    render: (s) => <><div className="text-slate-500">{s.last_swept_at ? fmt(s.last_swept_at) : 'Never run'}</div>{s.last_sweep_summary && <div className="text-slate-800">{s.last_sweep_summary}</div>}</> },
                                { key: 'state', label: 'Now', render: (s) => <><span className={s.sweep_requested_at || s.is_due ? 'text-indigo-700' : 'text-slate-600'}>{s.state}</span>
                                    {s.sweep_requested_at && <div className="text-xs text-slate-400">asked {fmt(s.sweep_requested_at)}</div>}</> },
                                { key: 'actions', label: '', sortable: false, csv: false, search: false, render: (s) => (s.can_retry && (
                                    <button onClick={() => retry(s)} disabled={busyId === s.search_id} className="rounded bg-slate-800 px-3 py-1 text-xs font-semibold text-white disabled:opacity-50">Retry</button>)) },
                            ]} />
                    </div>
                )}
            </section>

            {/* Other background jobs */}
            <section className="rounded-lg bg-white p-4 shadow md:p-6">
                <h2 className="mb-3 text-lg font-semibold text-slate-800">Other background jobs</h2>
                <div className="grid gap-4 md:grid-cols-2">
                    {data.jobs.map((j) => (
                        <div key={j.key} className={`rounded border p-3 ${(TONE[j.available ? j.tone : 'grey'] || TONE.grey).box}`}>
                            <div className="flex items-center gap-2">
                                <Dot tone={j.available ? j.tone : 'grey'} />
                                <h3 className="font-semibold text-slate-800">{j.name}</h3>
                                {j.schedule && <span className="text-xs text-slate-500">· {j.schedule}</span>}
                            </div>
                            {!j.available ? <p className="mt-1 text-sm text-slate-600">Not installed on this server.</p> : (
                                <ul className="mt-1 space-y-0.5 text-sm text-slate-700">
                                    {(j.lines || []).map((l, i) => <li key={i}>{l}</li>)}
                                </ul>
                            )}
                            {j.available && j.link && <Link to={j.link} className="mt-1 inline-block text-xs text-indigo-700 hover:underline">Open</Link>}
                        </div>
                    ))}
                </div>
                <p className="mt-3 text-xs text-slate-500">Read-only. Nightly backups are not listed here: they run on the server and are not recorded in the database.</p>
            </section>
        </div>
    );
};

export default SystemStatusPage;
